import { useMemo, useState } from 'react'
import {
  Search,
  Trash2,
  CheckCircle2,
  ShieldCheck,
  Edit,
  Eye
} from 'lucide-react'
import clsx from 'clsx'

import ModalEditarProgramado from './ModalEditarProgramado'
import ModalVistaPreviaPDFProgramado from './ModalVistaPreviaPDFProgramado'
import { useGlobalContext } from '../../../../../../../../context/GlobalState'

const formatCLP = value =>
  Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

const getFrecuenciaLabel = frec => {
  const map = {
    daily: 'Diario',
    weekly: 'Semanal',
    monthly: 'Mensual',
    quarterly: 'Trimestral',
    semiannual: 'Semestral',
    yearly: 'Anual'
  }
  return map[frec] || frec || '—'
}

const ActionButton = ({ label, onClick, tone = 'slate', children }) => {
  const toneClass =
    tone === 'red'
      ? 'text-red-600 hover:bg-red-50'
      : tone === 'blue'
        ? 'text-blue-600 hover:bg-blue-50'
        : 'text-slate-600 hover:bg-slate-100'

  return (
    <button
      type='button'
      onClick={onClick}
      className={`rounded-lg p-2 transition ${toneClass}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

const calculateTotal = (details, docType, currency) => {
  if (currency === 'UF') {
    return details.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0)
  }

  const ivaFactor = String(docType) === '33' ? 1.19 : 1

  return details.reduce((acc, item) => {
    const price = Number(item.unit_price) || 0
    const qty = Number(item.quantity) || 0
    return acc + price * qty * ivaFactor
  }, 0)
}

const getProgramacion = row => {
  if (row.frecuencia === 'weekly') return `Día semana ${row.dia_de_semana || 1}`
  if (row.frecuencia === 'daily') return 'Todos los días'
  return `Día ${row.dia_del_mes || 1}`
}

const DTE_NAMES = {
  33: 'Factura Electrónica',
  34: 'Factura Exenta',
  61: 'Nota de Crédito',
  56: 'Nota de Débito'
}

const calculateNextEmission = row => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let anchorDate = new Date(today)
  if (row.fecha_inicio) {
    const parts = row.fecha_inicio.split('-')
    if (parts.length === 3) {
      const parsedDate = new Date(parts[0], parts[1] - 1, parts[2])
      if (parsedDate > anchorDate) {
        anchorDate = parsedDate
      }
    }
  }

  const diaMes = row.dia_del_mes || 15
  const jsTargetDay = row.dia_de_semana === 7 ? 0 : row.dia_de_semana || 1

  const applyFrequencyJump = (date, freq) => {
    if (freq === 'monthly') date.setMonth(date.getMonth() + 1)
    else if (freq === 'quarterly') date.setMonth(date.getMonth() + 3)
    else if (freq === 'semiannual' || freq === 'biannual') date.setMonth(date.getMonth() + 6)
    else if (freq === 'yearly') date.setFullYear(date.getFullYear() + 1)
  }

  let finalDate = new Date(anchorDate)

  if (['monthly', 'quarterly', 'semiannual', 'biannual', 'yearly'].includes(row.frecuencia)) {
    // Avoid month overflow with day setting (e.g. Feb 31), though JS auto-adjusts, setting day last works well
    finalDate = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), diaMes)
    if (finalDate < anchorDate) {
      applyFrequencyJump(finalDate, row.frecuencia)
    }
  } else if (row.frecuencia === 'weekly') {
    let currentDay = finalDate.getDay()
    let daysToAdd = jsTargetDay - currentDay
    if (daysToAdd < 0) daysToAdd += 7
    finalDate.setDate(finalDate.getDate() + daysToAdd)
  }

  const dd = String(finalDate.getDate()).padStart(2, '0')
  const mm = String(finalDate.getMonth() + 1).padStart(2, '0')
  const yyyy = finalDate.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

export default function Paso1PrevisualizacionProgramados({
  fileName,
  rows,
  onBack,
  onContinue,
  onDeleteRow,
  onUpdateRow
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [editingRowIndex, setEditingRowIndex] = useState(null)
  const [previewRowIndex, setPreviewRowIndex] = useState(null)
  const { masterEntityData } = useGlobalContext()

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return rows

    return rows.filter(row =>
      [
        row.business_name,
        row.rut_receptor,
        row.tipo_documento,
        row.frecuencia,
        row.currency,
        row.fecha_inicio
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(term))
    )
  }, [rows, searchTerm])

  const styles = {
    table: 'w-full border-collapse text-[0.725rem] leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md',
    th: 'border-b border-slate-200 py-4 px-4 text-center uppercase text-[0.6rem] font-bold text-slate-500',
    tr: 'border-b border-slate-100 hover:bg-slate-50/50 transition-colors',
    td: 'px-4 py-2.5 align-middle',
    boldText: 'font-bold text-[0.7rem] text-slate-900 text-center',
    text: 'text-[0.64rem] font-medium text-slate-500',
    secondaryText: 'text-[0.6rem] text-slate-400'
  }

  return (
    <div className='flex flex-col h-full min-h-0 overflow-hidden p-1 space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-2'>
        <div className='min-w-0'>
          <h3 className='text-md font-bold text-slate-900 leading-tight whitespace-nowrap'>
            Previsualización de programaciones
          </h3>
          <p className='mt-0.5 text-[0.7rem] text-slate-500 font-medium'>
            A continuación podrá revisar un resumen del total de documentos que se van a emitir.
          </p>
        </div>

        <div className='relative w-full sm:w-60'>
          <Search
            size={14}
            className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400'
          />
          <input
            type='text'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder='Buscar...'
            className='w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-[0.72rem] text-slate-700 outline-none transition focus:border-primary/40'
          />
        </div>
      </div>

      <div className='flex-1 min-h-0 border border-slate-200 rounded-xl shadow-sm overflow-hidden bg-white flex flex-col'>
        <div className='overflow-auto flex-1 custom-scrollbar'>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={clsx(styles.th, 'text-left pl-6 w-[28%]')}>Cliente</th>
                <th className={clsx(styles.th, 'w-[18%]')}>Documento</th>
                <th className={clsx(styles.th, 'w-[14%]')}>Frecuencia</th>
                <th className={clsx(styles.th, 'w-[12%]')}>Próxima emisión</th>
                <th className={clsx(styles.th, 'w-[14%] text-right')}>Monto</th>
                <th className={clsx(styles.th, 'w-[14%]')}>Acciones</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-50'>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className='py-20 text-center text-slate-400 font-medium'>
                    No se encontraron resultados.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, idx) => {
                  const originalIndex = rows.indexOf(row)
                  const total = calculateTotal(row.details || [], row.tipo_documento, row.currency)
                  const code = String(row.tipo_documento || '33')

                  return (
                    <tr key={idx} className={styles.tr}>
                      <td className={clsx(styles.td, 'pl-6')}>
                        <div className='flex items-center flex-col gap-0.5'>
                          <span
                            className={clsx(styles.boldText, 'truncate block max-w-[240px]')}
                            title={row.business_name}
                          >
                            {row.business_name || 'Sin razón social'}
                          </span>
                          <span className={clsx(styles.secondaryText)}>
                            {row.rut_receptor || '—'}
                          </span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className='flex flex-col items-center gap-1.5'>
                          <span
                            className={clsx(
                              'inline-flex px-2 py-0.5 rounded-md text-[9px] font-black border',
                              code === '34'
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                : 'bg-blue-50 text-blue-600 border-blue-100'
                            )}
                          >
                            {DTE_NAMES[code] || `Tipo ${code}`}
                          </span>
                          <span className={styles.secondaryText}>
                            {(row.details || []).length} ítems
                          </span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className='flex flex-col items-center'>
                          <span className={clsx(styles.text, 'text-slate-900 font-bold')}>
                            {getFrecuenciaLabel(row.frecuencia)}
                          </span>
                          <span className={styles.secondaryText}>{getProgramacion(row)}</span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <span className={clsx(styles.text, 'text-center block text-[10px]')}>
                          {calculateNextEmission(row)}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <div className='flex flex-col items-end leading-none'>
                          <span className={clsx(styles.boldText, 'text-[0.8rem]')}>
                            {row.currency === 'UF' ? '' : '$'}
                            {formatCLP(total)} {row.currency === 'UF' ? 'UF' : ''}
                          </span>
                          {row.currency !== 'UF' && (
                            <div className='flex flex-col text-[0.58rem] mt-1 items-end gap-0.5'>
                              {code === '33' && (
                                <span className='text-rose-500 font-semibold'>
                                  IVA: ${formatCLP(total - total / 1.19)}
                                </span>
                              )}
                              <span className='text-slate-400'>
                                Neto: ${formatCLP(code === '33' ? total / 1.19 : total)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className='flex justify-center gap-1.5'>
                          <ActionButton
                            label='Editar'
                            onClick={() => setEditingRowIndex(originalIndex)}
                            tone='blue'
                          >
                            <Edit size={16} />
                          </ActionButton>
                          <ActionButton
                            label='Ver PDF'
                            onClick={() => setPreviewRowIndex(originalIndex)}
                            tone='slate'
                          >
                            <Eye size={16} />
                          </ActionButton>
                          <ActionButton
                            label='Eliminar'
                            onClick={() => onDeleteRow(originalIndex)}
                            tone='red'
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rows.length > 0 && (
        <div className='mt-2 mb-3 flex flex-col items-center justify-center shrink-0'>
          <div className='rounded-2xl border border-blue-100 bg-blue-50/20 p-4 shadow-sm'>
            <div className='flex flex-col items-center text-center'>
              <div className='mb-1.5 flex h-8 w-8 items-center justify-center rounded-xl bg-[#000033] text-white shadow-sm'>
                <ShieldCheck size={18} />
              </div>

              <h3 className='text-sm font-bold text-slate-900'>Confirmación de emisión</h3>
            </div>

            <div className='mt-1 rounded-xl bg-white p-2.5 border border-slate-100 shadow-sm'>
              <label className='flex cursor-pointer items-center gap-3'>
                <div className='relative flex items-center shrink-0'>
                  <input
                    type='checkbox'
                    checked={accepted}
                    onChange={e => setAccepted(e.target.checked)}
                    className='peer h-4.5 w-4.5 cursor-pointer appearance-none rounded-md border-2 border-slate-300 transition-all checked:border-primary checked:bg-primary'
                  />
                </div>
                <span
                  className={clsx(
                    'text-[0.85rem] font-medium transition-colors',
                    accepted ? 'text-slate-900' : 'text-slate-600'
                  )}
                >
                  He revisado los documentos y autorizo su programación al SII.
                </span>
              </label>
            </div>

            <div className='mt-3 flex justify-center'>
              <button
                type='button'
                onClick={() => {
                  if (accepted) onContinue()
                }}
                disabled={!accepted || rows.length === 0}
                className={clsx(
                  'flex items-center gap-2 rounded-full text-white text-sm px-4 py-2 shadow transition-all duration-300',
                  accepted
                    ? 'bg-accent2 hover:bg-accent2/80 hover:shadow-lg active:scale-95'
                    : 'bg-[#cbd5e1] cursor-not-allowed opacity-70'
                )}
              >
                <span>Confirmar programación</span>
                <CheckCircle2 size={13} className='transition-transform' />
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalEditarProgramado
        open={editingRowIndex !== null}
        onClose={() => setEditingRowIndex(null)}
        row={editingRowIndex !== null ? rows[editingRowIndex] : null}
        onSave={newRow => {
          if (onUpdateRow) onUpdateRow(editingRowIndex, newRow)
          setEditingRowIndex(null)
        }}
      />

      <ModalVistaPreviaPDFProgramado
        open={previewRowIndex !== null}
        onClose={() => setPreviewRowIndex(null)}
        row={previewRowIndex !== null ? rows[previewRowIndex] : null}
        masterEntityData={masterEntityData}
      />

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; border: 2px solid #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  )
}
